import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect, useContext } from 'react';

import BackIcon from '~/shared/assets/icons/circled-back.svg';
import PageContainer from '~/shared/components/PageContainer';
import { VolumeResponse } from '~/shared/types/api';
import { useXFetch } from '~/shared/utils/useXFetch';
import Loader from '~/shared/components/Loader';
import Typography from '~/shared/components/Typography';

import { baseUrl, datafyErrorToast } from '../FleetManagerPage/helpers';
import { refetchIntervalMs } from '../common/helpers';
import { DatafySwitchContext } from '../DatafySwitchProvider';

import GeneralInfo from './GeneralInfo';
import DatafyVolumes from './DatafyVolumes';
import Utilization from './Utilization';

export default function VolumePage() {
  const [hasNoData, setHasNoData] = useState(false);
  const { id: volumeId } = useParams();

  const qs = new URLSearchParams({
    filters: JSON.stringify({
      field: 'volumeId',
      value: volumeId,
      op: 'equals',
    }),
  });

  const { datafySwitchVolumes, removeDatafySwitchVolume } = useContext(DatafySwitchContext);

  const api = useXFetch();
  const { data, isLoading, error } = useQuery({
    queryKey: ['getVolume', volumeId],
    queryFn: async () => {
      const res = await api<VolumeResponse>(`${baseUrl}?${qs.toString()}`);
      const currentVolume = res.volumes[0];
      const prevVolume = data;
      const machineVolume = datafySwitchVolumes?.find((v) => v.volumeId === volumeId);
      if (!currentVolume) {
        setHasNoData(true);
      }

      if (currentVolume.monitoring !== prevVolume?.monitoring) {
        const datafyProcessFailed =
          !machineVolume &&
          ((currentVolume?.monitoring === 'Monitored' && prevVolume?.monitoring === 'Migrating') ||
            (currentVolume?.monitoring === 'Managed' && prevVolume?.monitoring === 'Unmigrating'));

        if (datafyProcessFailed) {
          datafyErrorToast(
            currentVolume.volumeId,
            currentVolume?.monitoring === 'Monitored' ? 'datafying' : 'undatafying'
          );
        }

        const datafyProcessSucceeded =
          (machineVolume?.state === 'datafying' && currentVolume?.monitoring === 'Managed') ||
          (machineVolume?.state === 'undatafying' && currentVolume?.monitoring === 'Monitored') ||
          (currentVolume?.monitoring === 'Migrating' && prevVolume?.monitoring === 'Monitored') ||
          (currentVolume?.monitoring === 'Unmigrating' && prevVolume?.monitoring === 'Managed');

        if (datafyProcessSucceeded) {
          removeDatafySwitchVolume(currentVolume?.volumeId);
        }
      }

      return currentVolume;
    },
    refetchOnWindowFocus: false,
    refetchInterval: !hasNoData ? refetchIntervalMs : false,
  });

  useEffect(() => {
    if (error) {
      setHasNoData(true);
    }
  }, [error]);

  if (isLoading) {
    return (
      <div className="flex flex-1 justify-center items-center">
        <Loader variant="xl" />
      </div>
    );
  }

  if (data && volumeId && !hasNoData) {
    return (
      <PageContainer>
        <Header id={volumeId} />

        <div className="flex-1 grid grid-cols-2 gap-[24px]">
          <GeneralInfo volume={data} />
          <div className="grid grid-rows-2 gap-[24px] min-h-[866px]">
            <Utilization volumeId={data?.volumeId} />
            <DatafyVolumes volume={data} />
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <div className="h-full flex items-center justify-center">
      <Typography variant="H5">No Results</Typography>
    </div>
  );
}

function Header({ id }: { id: string }) {
  const navigate = useNavigate();

  return (
    <div className="flex gap-[10px] items-center mb-[30px]">
      <BackIcon className="w-[30px] h-[30px] cursor-pointer" onClick={() => navigate(-1)} />

      <Typography variant="H3" color="text-white">
        Volume ID: <span className="font-bold">{id}</span>
      </Typography>
    </div>
  );
}
